import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineExclamation,
  HiOutlineChevronLeft,
  HiOutlineX,
  HiOutlineUpload,
  HiOutlineDownload,
  HiOutlineDocumentText,
} from "react-icons/hi";
import {
  fetchCompetencies,
  fetchCompetency,
  createCompetency,
  updateCompetency,
  deleteCompetency,
  fetchCompetencyCategories,
  fetchCompetencyConfig,
  fetchCompetencyHeaders,
  fetchCompetencySubHeaders,
  fetchMasterData,
  fetchStateById,
  bulkUpload,
} from "../services/masterDataApi";
import { downloadXlsxTemplate } from "../services/xlsxTemplate";
import Toast from "../components/Toast";
import FilterDropdown from "../components/FilterDropdown";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import { useUserRole } from "../hooks/useUserRole";
import { useDebounce } from "../hooks/useDebounce";

/* ───── style tokens (re-used across fields) ────────────────────── */
const inputClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const selectClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const labelClass = "block text-sm font-medium text-[#24292f] mb-1";
const btnPrimary =
  "flex items-center gap-1.5 px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] transition-all";
const btnSecondary =
  "px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all";

/* ───── config ──────────────────────────────────────────────────── */

const EMPTY_FORM = {
  code: "",
  name: "",
  description: "",
  grade: "",
  subject: "",
  lms_link: "",
  category_name: "",
  header: "",
  sub_header: "",
  depends_on: [],
  state_id: "",
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPETENCY PAGE
   ═══════════════════════════════════════════════════════════════════ */
function Competency() {
  const { canManageCompetencies } = useUserRole();
  const [toast, setToast] = useState(null);

  // ── views: "list" | "form" — backed by the URL so a refresh in edit mode
  //    reopens the same competency instead of dropping back to the list ──
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "list";
  const urlEditId = searchParams.get("edit");
  // Competency has no standalone unique id — its real primary key in Neo4j is
  // the (code, state_id) pair (the same code can legitimately repeat across
  // states), so the URL needs both to unambiguously restore the right one.
  const urlEditStateId = searchParams.get("editState");
  const setView = useCallback((v, params = {}) => {
    if (v === "list") {
      setSearchParams({});
    } else if (v === "form" && params.edit) {
      const next = { view: "form", edit: params.edit };
      if (params.editState) next.editState = params.editState;
      setSearchParams(next);
    } else {
      setSearchParams({ view: v });
    }
  }, [setSearchParams]);

  // ── list state ──
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ state_id: "", grade: "", subject: "", category: "" });
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // ── pagination state ──
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ── state management (for PL auto-select) ──
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(true);

  // ── form state ──
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingCode, setEditingCode] = useState(null);
  const [originalStateId, setOriginalStateId] = useState(null); // Track original state when editing
  const [saving, setSaving] = useState(false);

  // ── dropdown data (fetched from API) ──
  const [categories, setCategories] = useState([]);
  const [allCompetencies, setAllCompetencies] = useState([]);
  const [configData, setConfigData] = useState({ grades: [], subjects: [] });
  const [headersList, setHeadersList] = useState([]);
  const [subHeadersList, setSubHeadersList] = useState([]);

  // ── delete confirm ──
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // ── bulk upload ──
  const fileInputRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  /* ── loaders ───────────────────────────────────────────────────── */

  const loadConfig = useCallback(async () => {
    try {
      const data = await fetchCompetencyConfig();
      setConfigData(data);
    } catch (err) {
      console.error("Failed to load competency config", err);
    }
  }, []);

  const loadCompetencies = useCallback(async (page = currentPage, searchQuery = debouncedSearch) => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search: searchQuery };
      if (filters.state_id) params.state_id = filters.state_id;
      if (filters.grade) params.grade = filters.grade;
      if (filters.subject) params.subject = filters.subject;
      if (filters.category) params.category = filters.category;
      const res = await fetchCompetencies(params);
      if (requestId !== loadRequestIdRef.current) return; // superseded by a newer request
      setCompetencies(res.data || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
      setCurrentPage(res.current_page || page);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error("Failed to load competencies", err);
      setCompetencies([]);
      setTotalCount(0);
      setTotalPages(1);
      setToast({ type: "error", message: "Failed to load competencies" });
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  }, [filters, currentPage, pageSize, debouncedSearch]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetchCompetencyCategories({ page: 1, page_size: 100 });
      setCategories(res.data || []);
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  }, []);

  // Form-only data (duplicate-code check + dependency labels): fetched when
  // the form is open, and only re-fetched if the State filter it is scoped
  // to has changed since the last fetch.
  const allCompetenciesLoadedFor = useRef(null);
  const loadAllCompetencies = useCallback(async () => {
    const stateKey = filters.state_id || "";
    if (allCompetenciesLoadedFor.current === stateKey) return;
    allCompetenciesLoadedFor.current = stateKey;
    try {
      const params = { page: 1, page_size: 100 };
      if (filters.state_id) params.state_id = filters.state_id;
      const res = await fetchCompetencies(params);
      setAllCompetencies(res.data || []);
    } catch (err) {
      allCompetenciesLoadedFor.current = null;
      console.error(err);
    }
  }, [filters.state_id]);

  // Data changed — refetch on the next form open (the list view doesn't use it).
  const invalidateAllCompetencies = () => { allCompetenciesLoadedFor.current = null; };

  // Header list feeds only the form's Header dropdown — fetched once, the
  // first time the form needs it, and shared by concurrent callers.
  const headersPromiseRef = useRef(null);
  const loadHeaders = useCallback(() => {
    if (!headersPromiseRef.current) {
      headersPromiseRef.current = fetchCompetencyHeaders({ page: 1, page_size: 100 })
        .then((res) => {
          const list = res.data || [];
          setHeadersList(list);
          return list;
        })
        .catch((err) => {
          console.error("Failed to load headers", err);
          headersPromiseRef.current = null;
          return [];
        });
    }
    return headersPromiseRef.current;
  }, []);

  const loadSubHeaders = useCallback(async (headerId) => {
    if (!headerId) {
      setSubHeadersList([]);
      return;
    }
    try {
      const data = await fetchCompetencySubHeaders(headerId);
      setSubHeadersList(data);
    } catch (err) {
      console.error("Failed to load sub-headers", err);
      setSubHeadersList([]);
    }
  }, []);

  // Load states on mount and auto-select for SPL
  useEffect(() => {
    const loadStates = async () => {
      try {
        // A single page_size=100 fetch silently dropped states past the
        // first (alphabetical) page once the states table grew past 100 rows
        // — competencies assigned to a later state showed a raw state_id
        // instead of a name, and the state filter couldn't find them at all.
        // Page through everything instead of assuming it fits in one page.
        let stateList = [];
        let page = 1;
        while (true) {
          const data = await fetchMasterData("states", { page, page_size: 200 });
          const results = data.data || data || [];
          stateList = stateList.concat(results);
          if (page >= (data.total_pages || 1)) break;
          page++;
        }
        // Non-ADMIN users only get their own state(s) from the API, so the
        // State filter already offers just their area — nothing is pre-selected.
        setStates(stateList);
      } catch (err) {
        console.error("Failed to load states", err);
      } finally {
        setStatesLoading(false);
      }
    };
    loadStates();
  }, []);

  // Config (grades/subjects) and categories feed the Filters panel options.
  useEffect(() => {
    loadConfig();
    loadCategories();
  }, [loadConfig, loadCategories]);

  // Headers + all-competencies are only used inside the add/edit form.
  useEffect(() => {
    if (view !== "form") return;
    loadHeaders();
    loadAllCompetencies();
  }, [view, loadHeaders, loadAllCompetencies]);

  useEffect(() => {
    setCurrentPage(1);
    loadCompetencies(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.grade, filters.subject, filters.category, filters.state_id, debouncedSearch, pageSize]);

  /* ── filter handler ────────────────────────────────────────────── */
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  /* ── form helpers ──────────────────────────────────────────────── */

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, state_id: filters.state_id || "" });
    setEditingCode(null);
    setOriginalStateId(null);
    setSubHeadersList([]);
    setView("form");
  };

  const openEdit = async (code, stateId, row = null) => {
    // A direct click already knows what it's opening — mark the URL-restore
    // effect as satisfied so it doesn't also redundantly re-fetch this same
    // competency once setView() below updates the URL.
    hasRestoredFromUrl.current = true;
    try {
      // A list row already carries every field the form needs (same RETURN
      // clause as the single-competency GET) — only fetch on URL restore.
      const comp = row || await fetchCompetency(code, stateId);
      const compStateId = comp.state_id || stateId;
      setForm({
        code: comp.code,
        name: comp.name,
        description: comp.description || "",
        grade: comp.grade || "",
        subject: comp.subject || "",
        lms_link: comp.lms_link || "",
        category_name: comp.category || "",
        header: comp.header || "",
        sub_header: comp.sub_header || "",
        depends_on: comp.depends_on || [],
        state_id: compStateId,
      });
      setOriginalStateId(compStateId); // Store original state_id
      // Pre-load subheaders for the selected header
      if (comp.header) {
        const headers = await loadHeaders();
        const hdr = headers.find((h) => h.name === comp.header);
        if (hdr) {
          loadSubHeaders(hdr.id);
        }
      } else {
        setSubHeadersList([]);
      }
      setEditingCode(code);
      setView("form", { edit: code, editState: compStateId });
    } catch (err) {
      console.error("Load error:", err);
      let errorMessage = "Failed to load competency";

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join("; ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }

      setToast({
        type: "error",
        message: errorMessage,
      });
    }
  };

  // Restore edit view from URL on refresh — openEdit already fetches the
  // competency itself, so just call it with the code/state from the URL.
  const hasRestoredFromUrl = useRef(false);
  useEffect(() => {
    if (hasRestoredFromUrl.current) return;
    if (view === "form" && urlEditId) {
      hasRestoredFromUrl.current = true;
      openEdit(urlEditId, urlEditStateId);
    } else if (view === "form" && !urlEditId) {
      hasRestoredFromUrl.current = true;
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, urlEditId]);

  // Get weight from selected category
  const selectedCategoryWeight = (() => {
    if (!form.category_name) return null;
    const cat = categories.find((c) => c.name === form.category_name);
    return cat ? cat.weight : null;
  })();

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setToast({ type: "error", message: "Code and Name are required" });
      return;
    }
    if (!/\p{L}/u.test(form.name.trim())) {
      setToast({ type: "error", message: "Name must contain at least one letter (not just numbers, spaces, or symbols)" });
      return;
    }
    if (!form.state_id) {
      setToast({ type: "error", message: "State is required" });
      return;
    }
    
    // Frontend validation: Check for duplicate code in the same state. Codes
    // are compared ignoring case — "c101" and "C101" are the same code (the
    // backend enforces the same rule).
    if (!editingCode || (editingCode && form.state_id !== originalStateId)) {
      const typedCode = (form.code || "").trim().toLowerCase();
      const duplicate = allCompetencies.find(
        (c) => (c.code || "").toLowerCase() === typedCode && c.state_id === Number(form.state_id) && c.code !== editingCode
      );
      if (duplicate) {
        setToast({ 
          type: "error", 
          message: duplicate.code === form.code
            ? `Competency code "${form.code}" already exists in this state`
            : `Competency code "${form.code}" already exists in this state as "${duplicate.code}"`
        });
        return;
      }
    }
    
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        name: form.name.trim(),
        description: form.description || "",
        grade: form.grade || null,
        subject: form.subject || null,
        lms_link: form.lms_link || null,
        header: form.header || null,
        sub_header: form.sub_header || null,
        category_name: form.category_name || null,
        depends_on: form.depends_on || [],
        state_id: Number(form.state_id),
      };
      if (editingCode) {
        const { code, ...updatePayload } = payload; // eslint-disable-line no-unused-vars
        // Use originalStateId as query param (current state), state_id in payload (new state)
        await updateCompetency(editingCode, updatePayload, originalStateId);
        setToast({ type: "success", message: "Competency updated" });
        setView("list");
        loadCompetencies();
        invalidateAllCompetencies();
      } else {
        await createCompetency(payload);
        setToast({ type: "success", message: "Competency created" });
        setView("list");
        loadCompetencies();
        invalidateAllCompetencies();
      }
    } catch (err) {
      console.error("Save error:", err);
      let errorMessage = "Failed to save competency";
      
      // Handle validation errors from FastAPI
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join("; ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setToast({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── delete ────────────────────────────────────────────────────── */

  const handleDelete = async (code, stateId) => {
    try {
      await deleteCompetency(code, stateId);
      setShowDeleteConfirm(null);
      setToast({ type: "success", message: "Competency deleted" });
      loadCompetencies();
      invalidateAllCompetencies();
      if (view !== "list") setView("list");
    } catch (err) {
      console.error("Delete error:", err);
      let errorMessage = "Failed to delete";
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join("; ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setToast({
        type: "error",
        message: errorMessage,
      });
    }
  };

  /* ── bulk upload handlers ──────────────────────────────────────── */
  const openUploadModal = () => {
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadModal(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && !f.name.endsWith(".xlsx")) {
      setToast({ type: "error", message: "Only .xlsx files are accepted" });
      return;
    }
    setUploadFile(f || null);
    setUploadResult(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && !f.name.endsWith(".xlsx")) {
      setToast({ type: "error", message: "Only .xlsx files are accepted" });
      return;
    }
    setUploadFile(f || null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!uploadFile || uploadLoading) return;
    setUploadLoading(true);
    setUploadResult(null);
    try {
      const res = await bulkUpload("competencies", uploadFile);
      setUploadResult(res);
      if (res.success) {
        setToast({ type: "success", message: res.message || `Inserted ${res.inserted} competency(ies)` });
        loadCompetencies();
        invalidateAllCompetencies();
        setShowUploadModal(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed";
      setUploadResult({ success: false, errors: [], message: typeof detail === "string" ? detail : JSON.stringify(detail) });
      setToast({ type: "error", message: typeof detail === "string" ? detail : "Upload failed" });
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = () => {
    downloadXlsxTemplate(
      "competencies_template.xlsx",
      ["code", "name", "state_name", "grade", "subject", "description", "category_name", "header", "sub_header", "dependency_competency_code", "lms_link"],
      [
        // C101 has no dependency — it's the starting competency.
        ["C101", "Addition within 10", "Rajasthan", "Grade 1", "Mathematics", "Adding single digit numbers", "Numeracy", "Number Sense", "", "", "https://lms.example/c101"],
        // C102 requires C101 to be mastered first (dependency_competency_code).
        // dependency_competency_code accepts multiple competency codes separated by semicolons, e.g. "C101;C100".
        ["C102", "Addition within 20", "Rajasthan", "Grade 1", "Mathematics", "Adding numbers within 20, building on single-digit addition", "Numeracy", "Number Sense", "", "C101", "https://lms.example/c102"],
      ]
      // columns: code,name,state_name,grade,subject,description,category_name,header,sub_header,dependency_competency_code,lms_link
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ═══════════════════════════════════════════════════════════
           LIST VIEW
         ═══════════════════════════════════════════════════════════ */}
      {view === "list" && (
        <>
          {/* Toolbar */}
          <div className="bg-white rounded-t-xl border border-b-0 border-[#e5e7eb]">
            <div className="flex justify-between items-center px-6 py-4">
              <div className="relative flex items-center flex-1 max-w-sm">
                <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
                <input
                  type="text"
                  className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
                  placeholder="Search by code or name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
                    onClick={() => setSearch("")}
                  >
                    <HiOutlineX className="text-sm" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <FilterDropdown
                  filters={[
                    {
                      key: "state_id",
                      label: "State",
                      type: "select",
                      options: states.map((s) => ({ value: String(s.id), label: s.name })),
                    },
                    {
                      key: "grade",
                      label: "Grade",
                      type: "select",
                      options: configData.grades.map((g) => ({ value: g, label: g })),
                    },
                    {
                      key: "subject",
                      label: "Subject",
                      type: "select",
                      options: configData.subjects.map((s) => ({ value: s, label: s })),
                    },
                    {
                      key: "category",
                      label: "Category",
                      type: "select",
                      options: categories.map((c) => ({ value: c.name, label: c.name })),
                    },
                  ]}
                  values={filters}
                  onChange={handleFilterChange}
                />
                {canManageCompetencies && (
                  <>
                    <button
                      className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold rounded-xl cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
                      onClick={openUploadModal}
                    >
                      <HiOutlineUpload className="text-base" />
                      Bulk Upload
                    </button>
                    <button
                      className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-xl cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
                      onClick={openCreate}
                    >
                      <HiOutlinePlus className="text-base" />
                      Add Competency
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-b-xl border border-[#e5e7eb] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">State</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Grade</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Subject</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Category</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Depends On</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-[#f0f0f0]">
                      {[...Array(7)].map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-[#f0f0f0] rounded animate-pulse" style={{ width: j === 0 ? '60px' : j === 1 ? '140px' : '80px' }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : competencies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-[#8b949e]">
                      No competencies found.
                    </td>
                  </tr>
                ) : (
                  competencies.map((c) => (
                    <tr
                      key={`${c.code}-${c.state_id ?? "global"}`}
                      onClick={() => openEdit(c.code, c.state_id, c)}
                      className="border-b border-[#f0f0f0] hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono text-[#0078d4]">{c.code}</code>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#24292f]">{c.name}</td>
                      <td className="px-4 py-3 text-[#57606a]">
                        {c.state_id ? (
                          states.find(s => s.id === c.state_id)?.name || c.state_id
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-[#57606a]">{c.grade || "—"}</td>
                      <td className="px-4 py-3 text-[#57606a]">{c.subject || "—"}</td>
                      <td className="px-4 py-3 text-[#57606a]">{c.category || "—"}</td>
                      <td className="px-4 py-3 text-[#57606a]">
                        {c.depends_on && c.depends_on.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {c.depends_on.map((d) => (
                              <span key={d} className="inline-block text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-mono">
                                {d}
                              </span>
                            ))}
                          </div>
                        ) : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={(p) => loadCompetencies(p)}
              onPageSizeChange={handlePageSizeChange}
              entityName="competencies"
            />
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════
           FORM VIEW  (Create / Edit)
         ═══════════════════════════════════════════════════════════ */}
      {view === "form" && (
        <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
          <div className="sticky top-14 z-40 flex items-center justify-between px-6 py-4 border-b border-[#e5e7eb] bg-white rounded-t-xl">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setView("list")}
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
                title="Back"
              >
                <HiOutlineChevronLeft className="text-lg" />
              </button>
              <div>
                <h1 className="m-0 text-[15px] font-semibold text-[#24292f]">
                  {!canManageCompetencies
                    ? "Competency Details"
                    : editingCode
                    ? "Edit Competency"
                    : "Create Competency"}
                </h1>
                <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
                  {!canManageCompetencies ? "View competency information" : "Fill in the details below"}
                </p>
              </div>
            </div>
            {canManageCompetencies && editingCode && (
              <button
                onClick={() => setShowDeleteConfirm({ code: editingCode, name: form.name, state_id: originalStateId })}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>
          <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {/* Code */}
            <div>
              <label className={labelClass}>
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. NI1"
                value={form.code}
                disabled={!!editingCode || !canManageCompetencies}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className={`${inputClass} ${editingCode ? "bg-[#f6f8fa] text-[#8b949e]" : ""}`}
              />
            </div>

            {/* Name */}
            <div>
              <label className={labelClass}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Competency name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={!canManageCompetencies}
                className={inputClass}
              />
            </div>

            {/* State */}
            <div>
              <label className={labelClass}>
                State <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                value={String(form.state_id || "")}
                onChange={(id) => setForm({ ...form, state_id: id })}
                placeholder="— Select State —"
                fetchFn={(search, page = 1) => fetchMasterData("states", { page, page_size: 20, search })}
                resolveFn={(id) => fetchStateById(id).then((res) => res.data)}
                cacheNamespace="competency_state_id"
                disabled={!canManageCompetencies}
                variant="form"
              />
            </div>

            {/* Grade (from API config) */}
            <div>
              <label className={labelClass}>Grade</label>
              <select
                value={form.grade}
                onChange={(e) => setForm({ ...form, grade: e.target.value })}
                disabled={!canManageCompetencies}
                className={selectClass}
              >
                <option value="">— Select Grade —</option>
                {configData.grades.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            {/* Subject (from API config) */}
            <div>
              <label className={labelClass}>Subject</label>
              <select
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                disabled={!canManageCompetencies}
                className={selectClass}
              >
                <option value="">— Select Subject —</option>
                {configData.subjects.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Competency Category dropdown (weight auto from category) */}
            <div>
              <label className={labelClass}>Competency Category</label>
              <select
                value={form.category_name}
                onChange={(e) => setForm({ ...form, category_name: e.target.value })}
                disabled={!canManageCompetencies}
                className={selectClass}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}{c.weight != null ? ` (w=${c.weight})` : ""}
                  </option>
                ))}
              </select>
              {selectedCategoryWeight != null && (
                <p className="text-xs text-[#57606a] mt-1">
                  Weight from category: <strong>{selectedCategoryWeight}</strong>
                </p>
              )}
            </div>

            {/* Remediation URL */}
            <div>
              <label className={labelClass}>Remediation URL</label>
              <input
                type="url"
                placeholder="https://…"
                value={form.lms_link}
                onChange={(e) => setForm({ ...form, lms_link: e.target.value })}
                disabled={!canManageCompetencies}
                className={inputClass}
              />
            </div>

            {/* Header (dropdown) */}
            <div>
              <label className={labelClass}>Header</label>
              <select
                value={form.header}
                onChange={(e) => {
                  const selectedName = e.target.value;
                  setForm({ ...form, header: selectedName, sub_header: "" });
                  // find header id and load its subheaders
                  const hdr = headersList.find((h) => h.name === selectedName);
                  if (hdr) {
                    loadSubHeaders(hdr.id);
                  } else {
                    setSubHeadersList([]);
                  }
                }}
                disabled={!canManageCompetencies}
                className={selectClass}
              >
                <option value="">— Select header —</option>
                {headersList.map((h) => (
                  <option key={h.id} value={h.name}>{h.name}</option>
                ))}
              </select>
            </div>

            {/* Sub-header (dropdown, depends on header) */}
            <div>
              <label className={labelClass}>Sub-header</label>
              <select
                value={form.sub_header}
                onChange={(e) => setForm({ ...form, sub_header: e.target.value })}
                disabled={!form.header || !canManageCompetencies}
                className={`${selectClass} ${!form.header ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <option value="">{form.header ? "— Select sub-header —" : "— Select header first —"}</option>
                {subHeadersList.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Feedback — full width */}
            <div className="md:col-span-2">
              <label className={labelClass}>Feedback</label>
              <textarea
                rows={3}
                placeholder="Describe the competency…"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={!canManageCompetencies}
                className={inputClass}
              />
            </div>
          </div>

          {/* ── Relationship section ── */}
          <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
            <h3 className="text-sm font-semibold text-[#24292f] mb-4">Relationships</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Depends On — multi-select */}
              <div>
                <label className={labelClass}>Depends On</label>
                {form.depends_on.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {form.depends_on.map((depCode) => {
                      const depComp = allCompetencies.find((c) => c.code === depCode);
                      return (
                        <span
                          key={depCode}
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md font-medium"
                        >
                          {depCode}{depComp ? ` — ${depComp.name}` : ""}
                          {canManageCompetencies && (
                            <button
                              type="button"
                              onClick={() =>
                                setForm({
                                  ...form,
                                  depends_on: form.depends_on.filter((d) => d !== depCode),
                                })
                              }
                              className="ml-0.5 text-blue-400 hover:text-blue-700 font-bold"
                            >
                              ×
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
                <SearchableDropdown
                  key={form.depends_on.length}
                  value=""
                  onChange={(code) => {
                    if (code && !form.depends_on.includes(code)) {
                      setForm((prev) => ({ ...prev, depends_on: [...prev.depends_on, code] }));
                    }
                  }}
                  placeholder={form.depends_on.length > 0 ? "+ Add another…" : "— Select dependencies —"}
                  fetchFn={async (query, page = 1) => {
                    const params = { page, page_size: 20, search: query || "" };
                    if (form.state_id) params.state_id = form.state_id;
                    const res = await fetchCompetencies(params);
                    const filtered = (res.data || []).filter(
                      (c) => c.code !== form.code && !form.depends_on.includes(c.code)
                    );
                    return {
                      data: filtered.map((c) => ({ id: c.code, name: `${c.code} — ${c.name}` })),
                      total_count: res.total_count || filtered.length,
                    };
                  }}
                  variant="form"
                  emptyMessage="No matching competencies"
                  disabled={!canManageCompetencies}
                />
                <p className="text-xs text-[#8b949e] mt-1">Competencies that must be mastered before this one</p>
              </div>
            </div>
          </div>

          {/* ── Form actions ── */}
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[#e5e7eb]">
            <button onClick={() => setView("list")} className={btnSecondary}>
              {canManageCompetencies ? "Cancel" : "Back"}
            </button>
            {canManageCompetencies && (
              <button
                onClick={handleSave}
                disabled={saving || !form.code.trim() || !form.name.trim() || !form.state_id}
                className={`${btnPrimary} disabled:opacity-40`}
              >
                {saving ? "Saving…" : editingCode ? "Update Competency" : "Create Competency"}
              </button>
            )}
          </div>
          </div>
        </div>
        </div>
      )}

      {/* ═══ DELETE COMPETENCY CONFIRM MODAL ═══ */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <HiOutlineExclamation className="text-2xl text-red-600" />
            </div>
            <h3 className="text-base font-semibold text-[#24292f] mb-1">Delete Competency?</h3>
            <p className="text-sm text-[#57606a] mb-4">
              Are you sure you want to delete{" "}
              <strong>{showDeleteConfirm.code} — {showDeleteConfirm.name}</strong>?
              This will also remove all relationships.
            </p>
            <div className="flex justify-center gap-2">
              <button onClick={() => setShowDeleteConfirm(null)} className={btnSecondary}>Cancel</button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.code, showDeleteConfirm.state_id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ BULK UPLOAD MODAL ═══ */}
      {showUploadModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
          onClick={() => !uploadLoading && setShowUploadModal(false)}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape' && !uploadLoading) setShowUploadModal(false); }}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
              <div>
                <h3 className="m-0 text-base font-semibold text-[#24292f]">Bulk Upload Competencies</h3>
                <p className="m-0 mt-0.5 text-xs text-[#8b949e]">Upload an Excel file (.xlsx) to import competencies</p>
              </div>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                <HiOutlineX className="text-lg" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Template Download */}
              <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
                <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
                <div className="flex-1">
                  <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
                  <p className="m-0 text-xs text-[#57606a] mt-0.5">Includes code, name, state_name and more</p>
                </div>
                <button
                  className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                  onClick={downloadTemplate}
                >
                  <HiOutlineDownload className="text-sm" />
                  Download
                </button>
              </div>

              {/* File Input */}
              <div>
                <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
                <div
                  className={`relative border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer ${isDragging ? "border-[#0078d4] bg-[#eff6ff]" : "border-[#d0d5dd] hover:border-[#0078d4]"}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
                  {uploadFile ? (
                    <p className="m-0 text-sm font-medium text-[#24292f]">{uploadFile.name}</p>
                  ) : (
                    <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>
                  )}
                  <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB · Max 1000 rows</p>
                </div>
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`p-4 rounded-xl border ${
                  uploadResult.success
                    ? "bg-[#f0fdf4] border-[#bbf7d0]"
                    : "bg-red-50 border-red-200"
                }`}>
                  <p className={`m-0 text-sm font-semibold ${
                    uploadResult.success ? "text-[#16a34a]" : "text-red-700"
                  }`}>
                    {uploadResult.success
                      ? `Inserted ${uploadResult.inserted} record(s)${uploadResult.skipped ? ` · Skipped ${uploadResult.skipped}` : ""}`
                      : uploadResult.message || "Validation failed"}
                  </p>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                      {uploadResult.errors.map((err, i) => (
                        <p key={i} className="m-0 text-xs text-red-600">
                          Row {err.row}: {err.message}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button
                className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                {uploadResult?.success ? "Close" : "Cancel"}
              </button>
              {!uploadResult?.success && (
                <button
                  className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploadLoading}
                >
                  {uploadLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      <HiOutlineUpload className="text-base" />
                      Upload
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Competency;